/**
 * Gets all players from a specific team
 * @param {Array} players - Array of player objects
 * @param {String} teamName - Team name
 * @returns {Array} Array of player objects
 */
export const getTeamPlayers = (players, teamName) => {
  if (!players || players.length === 0 || !teamName) return [];
  
  // Get unique players by ID to avoid duplicates
  const uniquePlayers = {};
  
  players.forEach(player => {
    if (player['team.name'] === teamName) {
      const playerId = player['player.playerId'];
      if (!uniquePlayers[playerId]) {
        uniquePlayers[playerId] = player;
      }
    }
  });
  
  return Object.values(uniquePlayers);
};

/**
 * Gets team statistics for a specific stat category
 * @param {Array} players - Array of player objects
 * @param {String} teamName - Team name
 * @param {String} statCategory - Stat category
 * @returns {Object} Team statistics
 */
export const getTeamStats = (players, teamName, statCategory) => {
  if (!players || players.length === 0 || !teamName || !statCategory) {
    return { average: 0, total: 0, max: 0, min: 0 };
  }
  
  const teamPlayers = getTeamPlayers(players, teamName);
  
  if (teamPlayers.length === 0) {
    return { average: 0, total: 0, max: 0, min: 0 };
  }
  
  let total = 0;
  let max = 0;
  let min = Number.MAX_VALUE;
  
  teamPlayers.forEach(player => {
    const statValue = parseFloat(player[statCategory]) || 0;
    total += statValue;
    max = Math.max(max, statValue);
    min = Math.min(min, statValue);
  });
  
  const average = total / teamPlayers.length;
  
  return {
    average,
    total,
    max,
    min
  };
};

/**
 * Gets team performance by venue
 * @param {Array} players - Array of player objects
 * @param {Array} fixtures - Array of fixture objects
 * @param {String} teamName - Team name
 * @param {String} venue - Venue name
 * @returns {Object} Team performance by venue
 */
export const getTeamVenuePerformance = (players, fixtures, teamName, venue) => {
  if (!players || !fixtures || !teamName || !venue) {
    return { matches: 0, wins: 0, losses: 0, winPercentage: 0 };
  }
  
  // Filter fixtures by team and venue
  const teamFixtures = fixtures.filter(fixture => 
    (fixture['Home Team'] === teamName || fixture['Away Team'] === teamName) &&
    fixture['Location'] === venue
  );
  
  if (teamFixtures.length === 0) {
    return { matches: 0, wins: 0, losses: 0, winPercentage: 0 };
  }
  
  let wins = 0;
  let losses = 0;
  
  teamFixtures.forEach(fixture => {
    const isHomeTeam = fixture['Home Team'] === teamName;
    const homeScore = parseInt(fixture['Home Score'] || 0);
    const awayScore = parseInt(fixture['Away Score'] || 0);
    
    if (isHomeTeam) {
      if (homeScore > awayScore) wins++;
      else if (homeScore < awayScore) losses++;
    } else {
      if (awayScore > homeScore) wins++;
      else if (awayScore < homeScore) losses++;
    }
  });
  
  const matches = teamFixtures.length;
  const winPercentage = matches > 0 ? (wins / matches) * 100 : 0;
  
  return {
    matches,
    wins,
    losses,
    winPercentage
  };
};

/**
 * Gets team form (last N matches)
 * @param {Array} fixtures - Array of fixture objects
 * @param {String} teamName - Team name
 * @param {Number} lastN - Number of last matches to consider
 * @returns {Array} Array of form results ('W', 'L', 'D')
 */
export const getTeamForm = (fixtures, teamName, lastN = 5) => {
  if (!fixtures || fixtures.length === 0 || !teamName) return [];
  
  // Filter fixtures by team
  const teamFixtures = fixtures.filter(fixture => 
    (fixture['Home Team'] === teamName || fixture['Away Team'] === teamName) &&
    fixture['Home Score'] && fixture['Away Score'] // Only completed matches
  );
  
  // Sort fixtures by date (newest first)
  const sortedFixtures = [...teamFixtures].sort((a, b) => {
    const dateA = parseFixtureDate(a.Date);
    const dateB = parseFixtureDate(b.Date);
    return dateB - dateA;
  });
  
  // Take only the last N fixtures
  const lastNFixtures = sortedFixtures.slice(0, lastN);
  
  // Determine result for each fixture
  return lastNFixtures.map(fixture => {
    const isHomeTeam = fixture['Home Team'] === teamName;
    const homeScore = parseInt(fixture['Home Score'] || 0);
    const awayScore = parseInt(fixture['Away Score'] || 0);
    
    if (isHomeTeam) {
      if (homeScore > awayScore) return 'W';
      else if (homeScore < awayScore) return 'L';
      else return 'D';
    } else {
      if (awayScore > homeScore) return 'W';
      else if (awayScore < homeScore) return 'L';
      else return 'D';
    }
  });
};

/**
 * Gets team defense vs position stats
 * @param {Array} players - Array of player objects
 * @param {Array} fixtures - Array of fixture objects
 * @param {String} teamName - Team name
 * @param {String} position - Player position
 * @param {String} statCategory - Stat category
 * @returns {Object} Defense vs position stats
 */
export const getDefenseVsPosition = (players, fixtures, teamName, position, statCategory) => {
  if (!players || !fixtures || !teamName || !position || !statCategory) {
    return { average: 0, total: 0, matches: 0 };
  }
  
  // Filter fixtures by team
  const teamFixtures = fixtures.filter(fixture => 
    fixture['Home Team'] === teamName || fixture['Away Team'] === teamName
  );
  
  if (teamFixtures.length === 0) {
    return { average: 0, total: 0, matches: 0 };
  }
  
  let total = 0;
  let playerCount = 0;
  
  teamFixtures.forEach(fixture => {
    const opposingTeam = fixture['Home Team'] === teamName ? 
      fixture['Away Team'] : fixture['Home Team'];
    
    // Get players from opposing team with the specified position
    const opposingPlayers = players.filter(player => 
      player['team.name'] === opposingTeam && 
      player['player_position'] === position
    );
    
    opposingPlayers.forEach(player => {
      const statValue = parseFloat(player[statCategory]) || 0;
      total += statValue;
      playerCount++;
    });
  });
  
  const average = playerCount > 0 ? total / playerCount : 0;
  
  return {
    average,
    total,
    matches: teamFixtures.length
  };
};

/**
 * Gets team home/away splits
 * @param {Array} players - Array of player objects
 * @param {Array} fixtures - Array of fixture objects
 * @param {String} teamName - Team name
 * @param {String} statCategory - Stat category
 * @returns {Object} Home/away splits
 */
export const getTeamSplits = (players, fixtures, teamName, statCategory) => {
  if (!players || !fixtures || !teamName || !statCategory) {
    return { 
      home: { average: 0, total: 0, matches: 0 },
      away: { average: 0, total: 0, matches: 0 }
    };
  }
  
  // Filter fixtures by team
  const homeFixtures = fixtures.filter(fixture => fixture['Home Team'] === teamName);
  const awayFixtures = fixtures.filter(fixture => fixture['Away Team'] === teamName);
  
  // Calculate home stats
  let homeTotalStats = 0;
  let homePlayerCount = 0;
  
  homeFixtures.forEach(fixture => {
    const matchPlayers = players.filter(player => 
      player['team.name'] === teamName && 
      player['match_id'] === fixture['Match Number']
    );
    
    matchPlayers.forEach(player => {
      const statValue = parseFloat(player[statCategory]) || 0;
      homeTotalStats += statValue;
      homePlayerCount++;
    });
  });
  
  // Calculate away stats
  let awayTotalStats = 0;
  let awayPlayerCount = 0;
  
  awayFixtures.forEach(fixture => {
    const matchPlayers = players.filter(player => 
      player['team.name'] === teamName && 
      player['match_id'] === fixture['Match Number']
    );
    
    matchPlayers.forEach(player => {
      const statValue = parseFloat(player[statCategory]) || 0;
      awayTotalStats += statValue;
      awayPlayerCount++;
    });
  });
  
  const homeAverage = homePlayerCount > 0 ? homeTotalStats / homePlayerCount : 0;
  const awayAverage = awayPlayerCount > 0 ? awayTotalStats / awayPlayerCount : 0;
  
  return {
    home: {
      average: homeAverage,
      total: homeTotalStats,
      matches: homeFixtures.length
    },
    away: {
      average: awayAverage,
      total: awayTotalStats,
      matches: awayFixtures.length
    }
  };
};

/**
 * Team name mapping for standardization
 */
export const TEAM_MAPPING = {
  'Broncos': ['Broncos', 'Brisbane Broncos', 'Brisbane'],
  'Bulldogs': ['Bulldogs', 'Canterbury Bulldogs', 'Canterbury-Bankstown Bulldogs', 'Canterbury'],
  'Cowboys': ['Cowboys', 'North Queensland Cowboys', 'North Queensland'],
  'Dolphins': ['Dolphins', 'Redcliffe Dolphins', 'The Dolphins'],
  'Dragons': ['Dragons', 'St George Illawarra Dragons', 'St George Illawarra', 'St. George Illawarra'],
  'Eels': ['Eels', 'Parramatta Eels', 'Parramatta'],
  'Knights': ['Knights', 'Newcastle Knights', 'Newcastle'],
  'Panthers': ['Panthers', 'Penrith Panthers', 'Penrith'],
  'Rabbitohs': ['Rabbitohs', 'South Sydney Rabbitohs', 'South Sydney', 'Souths'],
  'Raiders': ['Raiders', 'Canberra Raiders', 'Canberra'],
  'Roosters': ['Roosters', 'Sydney Roosters', 'Eastern Suburbs'],
  'Sea Eagles': ['Sea Eagles', 'Manly Sea Eagles', 'Manly-Warringah Sea Eagles', 'Manly'],
  'Sharks': ['Sharks', 'Cronulla Sharks', 'Cronulla-Sutherland Sharks', 'Cronulla'],
  'Storm': ['Storm', 'Melbourne Storm', 'Melbourne'],
  'Titans': ['Titans', 'Gold Coast Titans', 'Gold Coast'],
  'Warriors': ['Warriors', 'New Zealand Warriors', 'NZ Warriors'],
  'Wests Tigers': ['Wests Tigers', 'Western Suburbs Tigers', 'Tigers']
};

/**
 * Normalizes team names to handle common variations
 * @param {string} teamName - The team name to normalize
 * @returns {string} The normalized team name
 */
export const normalizeTeamName = (teamName) => {
  if (!teamName) return '';
  
  const normalized = teamName.trim();
  
  // Check against the standard mapping first
  for (const [standard, variations] of Object.entries(TEAM_MAPPING)) {
    if (variations.some(v => v.toLowerCase() === normalized.toLowerCase())) {
      return standard;
    }
  }
  
  return normalized;
};

/**
 * Team logos mapping - updated to use normalized team names
 */
export const TEAM_LOGOS = {
  "Broncos": "https://upload.wikimedia.org/wikipedia/en/thumb/a/ad/Brisbane_Broncos_logo.svg/210px-Brisbane_Broncos_logo.svg.png",
  "Bulldogs": "https://upload.wikimedia.org/wikipedia/en/thumb/1/16/Canterbury-Bankstown_Bulldogs_logo.svg/160px-Canterbury-Bankstown_Bulldogs_logo.svg.png",
  "Cowboys": "https://upload.wikimedia.org/wikipedia/en/thumb/d/d0/North_Queensland_Cowboys_logo.svg/200px-North_Queensland_Cowboys_logo.svg.png",
  "Dolphins": "https://upload.wikimedia.org/wikipedia/en/thumb/a/ae/Dolphins_%28NRL%29_Logo.svg/480px-Dolphins_%28NRL%29_Logo.svg.png",
  "Dragons": "https://upload.wikimedia.org/wikipedia/en/thumb/b/bc/St._George_Illawarra_Dragons_logo.svg/150px-St._George_Illawarra_Dragons_logo.svg.png",
  "Eels":"https://upload.wikimedia.org/wikipedia/en/thumb/4/46/Parramatta_Eels_logo.svg/200px-Parramatta_Eels_logo.svg.png",
  "Knights":"https://upload.wikimedia.org/wikipedia/en/thumb/a/ae/Newcastle_Knights_logo.svg/180px-Newcastle_Knights_logo.svg.png",
  "Panthers":"https://upload.wikimedia.org/wikipedia/en/thumb/9/9e/Penrith_Panthers_Logo.svg/480px-Penrith_Panthers_Logo.svg.png",
  "Rabbitohs":"https://upload.wikimedia.org/wikipedia/en/thumb/1/18/South_Sydney_Rabbitohs.png/220px-South_Sydney_Rabbitohs.png",
  "Raiders":"https://upload.wikimedia.org/wikipedia/en/thumb/4/44/Canberra_Raiders_Logo.svg/220px-Canberra_Raiders_Logo.svg.png",
  "Roosters":"https://upload.wikimedia.org/wikipedia/en/thumb/8/8c/Sydney_Roosters_logo.svg/220px-Sydney_Roosters_logo.svg.png",
  "Sea Eagles":"https://upload.wikimedia.org/wikipedia/en/thumb/9/97/Manly_Warringah_Sea_Eagles_2023%E2%80%93present_logo.png/220px-Manly_Warringah_Sea_Eagles_2023%E2%80%93present_logo.png",
  "Sharks":"https://upload.wikimedia.org/wikipedia/en/thumb/7/7c/Cronulla-Sutherland_Sharks_logo.svg/240px-Cronulla-Sutherland_Sharks_logo.svg.png",
  "Storm":"https://upload.wikimedia.org/wikipedia/en/thumb/f/f6/Melbourne_Storm_Logo.svg/408px-Melbourne_Storm_Logo.svg.png",
  "Titans":"https://upload.wikimedia.org/wikipedia/en/thumb/7/78/Gold_Coast_Titans_logo.svg/220px-Gold_Coast_Titans_logo.svg.png",
  "Warriors":"https://upload.wikimedia.org/wikipedia/en/thumb/5/5b/Warriors_%28NRL%29_Logo.svg/480px-Warriors_%28NRL%29_Logo.svg.png",
  "Wests Tigers":"https://upload.wikimedia.org/wikipedia/en/thumb/e/ee/Wests_Tigers_2022_Logo.svg/240px-Wests_Tigers_2022_Logo.svg.png",
};

/**
 * Get team logo URL by team name
 * @param {String} teamName - Team name
 * @returns {String} Logo URL or empty string if not found
 */
export const getTeamLogo = (teamName) => {
  const normalized = normalizeTeamName(teamName);
  return TEAM_LOGOS[normalized] || '';
};

export const calculateDvpRanks = (players, selectedSeason, stats) => {
  if (!players?.length || !stats) return {};

  // Pre-filter players by season once
  const filteredPlayers = players.filter(p => p.season === selectedSeason);

  // Get unique teams once
  const teams = [...new Set(filteredPlayers.map(p => normalizeTeamName(p['team.name'])))].filter(Boolean);

  // Define position categories
  const positions = [
    'Fullback',
    'Winger',
    'Centre',
    'Five-Eighth',
    'Halfback',
    'Hooker',
    'Prop',
    '2nd Row',
    'Lock'
  ];

  // Map stat keys to actual data fields
  const statFieldMapping = {
    'points': 'points',
    'tries': 'tries',
    'all_run_metres': 'all_run_metres',
    'tackles_made': 'tackles_made',
    'line_breaks': 'line_breaks',
    'try_assists': 'try_assists',
    'tackle_breaks': 'tackle_breaks',
    'offloads': 'offloads',
    'errors': 'errors'
  };

  // Pre-process players by team for faster lookups
  const playersByTeam = new Map();
  filteredPlayers.forEach(p => {
    const teamName = normalizeTeamName(p['team.name']);
    if (!playersByTeam.has(teamName)) {
      playersByTeam.set(teamName, []);
    }
    playersByTeam.get(teamName).push(p);
  });

  // Calculate averages for each team against each position for each stat
  const teamPositionStats = {};

  teams.forEach(team => {
    teamPositionStats[team] = {};

    // Get all games against this team
    const gamesAgainstTeam = filteredPlayers.filter(p => {
      const homeTeam = normalizeTeamName(p.match_home_team);
      const awayTeam = normalizeTeamName(p.match_away_team);
      return homeTeam === team || awayTeam === team;
    });

    positions.forEach(position => {
      teamPositionStats[team][position] = {};

      // Filter games by position once per position
      const positionGames = gamesAgainstTeam.filter(p => {
        const playerTeam = normalizeTeamName(p['team.name']);
        if (playerTeam === team) return false;
        
        const playerPosition = p.position;
        return playerPosition === position;
      });

      // Calculate stats for all stat types at once
      if (positionGames.length === 0) {
        Object.keys(statFieldMapping).forEach(stat => {
          teamPositionStats[team][position][stat] = { average: 0, count: 0 };
        });
        return;
      }

      Object.keys(statFieldMapping).forEach(stat => {
        const total = positionGames.reduce((sum, game) => {
          return sum + (parseFloat(game[statFieldMapping[stat]]) || 0);
        }, 0);

        teamPositionStats[team][position][stat] = {
          average: total / positionGames.length,
          count: positionGames.length
        };
      });
    });
  });

  // Calculate ranks for each position and stat
  const positionRanks = {};
  
  teams.forEach(team => {
    positionRanks[team] = {};
    
    positions.forEach(position => {
      positionRanks[team][position] = {};
      
      Object.keys(statFieldMapping).forEach(stat => {
        const teamStats = teams
          .map(t => ({
            team: t,
            average: teamPositionStats[t][position][stat].average,
            count: teamPositionStats[t][position][stat].count
          }))
          .filter(s => s.count > 0);

        // Sort only if we have stats
        if (teamStats.length > 0) {
          teamStats.sort((a, b) => b.average - a.average);
          const teamIndex = teamStats.findIndex(s => s.team === team);
          if (teamIndex !== -1) {
            positionRanks[team][position][stat] = {
              rank: teamIndex + 1,
              average: teamStats[teamIndex].average,
              total: teamStats.length
            };
          }
        }
      });
    });
  });

  return positionRanks;
};

/**
 * Calculates team game statistics for both "for" and "against" scenarios
 * @param {Array} players - Array of player objects
 * @param {String} season - Season to filter by
 * @param {Number} lastNRounds - Optional, number of recent rounds to consider
 * @returns {Object} Object containing team stats for and against
 */
export const calculateTeamGameStats = (players, season, lastNRounds = null) => {
  if (!players?.length) return { for: [], against: [] };

  // Filter players by season
  const filteredPlayers = players.filter(p => p.season === season);
  if (filteredPlayers.length === 0) return { for: [], against: [] };

  // Get unique teams
  const teams = [...new Set(filteredPlayers.map(p => normalizeTeamName(p['team.name'])))];

  // Initialize stats objects
  const forStats = {};
  const againstStats = {};

  teams.forEach(team => {
    forStats[team] = {
      team,
      games: new Set(),
      gameStats: {}
    };
    againstStats[team] = {
      team,
      games: new Set(),
      gameStats: {}
    };
  });

  // Process each game
  filteredPlayers.forEach(p => {
    const team = p['team.name'];
    if (!team) return;

    // Get unique game identifier
    const gameId = `${p['match_id']}_${team}`;

    // Initialize game stats if not exists
    if (!forStats[team].gameStats[gameId]) {
      forStats[team].gameStats[gameId] = {
        points: 0,
        tries: 0,
        all_run_metres: 0,
        tackles_made: 0,
        line_breaks: 0,
        try_assists: 0,
        tackle_breaks: 0,
        offloads: 0,
        errors: 0
      };
    }

    // Add player's stats to game total
    forStats[team].games.add(gameId);
    forStats[team].gameStats[gameId].points += parseFloat(p['points']) || 0;
    forStats[team].gameStats[gameId].tries += parseFloat(p['tries']) || 0;
    forStats[team].gameStats[gameId].all_run_metres += parseFloat(p['all_run_metres']) || 0;
    forStats[team].gameStats[gameId].tackles_made += parseFloat(p['tackles_made']) || 0;
    forStats[team].gameStats[gameId].line_breaks += parseFloat(p['line_breaks']) || 0;
    forStats[team].gameStats[gameId].try_assists += parseFloat(p['try_assists']) || 0;
    forStats[team].gameStats[gameId].tackle_breaks += parseFloat(p['tackle_breaks']) || 0;
    forStats[team].gameStats[gameId].offloads += parseFloat(p['offloads']) || 0;
    forStats[team].gameStats[gameId].errors += parseFloat(p['errors']) || 0;

    // Get opponent team
    const homeTeam = p['match_home_team'];
    const awayTeam = p['match_away_team'];
    const opponentTeam = homeTeam === team ? awayTeam : homeTeam;

    if (opponentTeam) {
      // Initialize game stats for opponent if not exists
      if (!againstStats[opponentTeam].gameStats[gameId]) {
        againstStats[opponentTeam].gameStats[gameId] = {
          points: 0,
          tries: 0,
          all_run_metres: 0,
          tackles_made: 0,
          line_breaks: 0,
          try_assists: 0,
          tackle_breaks: 0,
          offloads: 0,
          errors: 0
        };
      }

      // Add player's stats to opponent's against totals
      againstStats[opponentTeam].games.add(gameId);
      againstStats[opponentTeam].gameStats[gameId].points += parseFloat(p['points']) || 0;
      againstStats[opponentTeam].gameStats[gameId].tries += parseFloat(p['tries']) || 0;
      againstStats[opponentTeam].gameStats[gameId].all_run_metres += parseFloat(p['all_run_metres']) || 0;
      againstStats[opponentTeam].gameStats[gameId].tackles_made += parseFloat(p['tackles_made']) || 0;
      againstStats[opponentTeam].gameStats[gameId].line_breaks += parseFloat(p['line_breaks']) || 0;
      againstStats[opponentTeam].gameStats[gameId].try_assists += parseFloat(p['try_assists']) || 0;
      againstStats[opponentTeam].gameStats[gameId].tackle_breaks += parseFloat(p['tackle_breaks']) || 0;
      againstStats[opponentTeam].gameStats[gameId].offloads += parseFloat(p['offloads']) || 0;
      againstStats[opponentTeam].gameStats[gameId].errors += parseFloat(p['errors']) || 0;
    }
  });

  // Calculate averages
  const calculateAverages = (statsObj) => {
    return Object.values(statsObj)
      .filter(team => team.games.size > 0)
      .map(team => {
        const gameStats = Object.values(team.gameStats);
        const numGames = team.games.size;

        return {
          team: team.team,
          points: (gameStats.reduce((sum, game) => sum + game.points, 0) / numGames).toFixed(1),
          tries: (gameStats.reduce((sum, game) => sum + game.tries, 0) / numGames).toFixed(1),
          all_run_metres: (gameStats.reduce((sum, game) => sum + game.all_run_metres, 0) / numGames).toFixed(1),
          tackles_made: (gameStats.reduce((sum, game) => sum + game.tackles_made, 0) / numGames).toFixed(1),
          line_breaks: (gameStats.reduce((sum, game) => sum + game.line_breaks, 0) / numGames).toFixed(1),
          try_assists: (gameStats.reduce((sum, game) => sum + game.try_assists, 0) / numGames).toFixed(1),
          tackle_breaks: (gameStats.reduce((sum, game) => sum + game.tackle_breaks, 0) / numGames).toFixed(1),
          offloads: (gameStats.reduce((sum, game) => sum + game.offloads, 0) / numGames).toFixed(1),
          errors: (gameStats.reduce((sum, game) => sum + game.errors, 0) / numGames).toFixed(1)
        };
      });
  };

  return {
    for: calculateAverages(forStats),
    against: calculateAverages(againstStats)
  };
}; 